import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addDays } from 'date-fns';
import { Body, Small } from 'syf-component-library/ui/typography';
import { formatCurrency, formatDateStringToObject } from 'syf-js-utilities/formats';
import formatDisplayAccount from 'syf-js-utilities/formats/formatDisplayAccount';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_REASON, DATA_TYPE } from '../../../../const/analytics';
import { FAS_EXTERNAL_LINK } from '../../../../const/iconProps';
import { paymentOptions, paymentTypes } from '../../../../const/paymentOptions';
import { isInstallmentLoan, isIntroRateLoanPromoAvailableForAutopay, isMonthly } from '../../../../helpers';
import getPaymentAmountFromOption from '../../../../helpers/getPaymentAmountFromOption';
import { isSecuredInstallmentLoanProduct } from '../../../../helpers/productTypeHelpers';
import { t } from '../../../../strings/i18n';
import { getPaymentAmountDisplay } from '../helpers';
import { PaymentMethodOption, PaymentOption } from '../subcomponents';
const Amount = styled(Body) `
  font-weight: inherit;
`;
const Label = styled(Small) `
  font-weight: inherit;
`;
const IconInfoContainer = styled.span `
  clip: rect(0, 0, 0, 0);
  overflow: hidden;
  position: absolute;
`;
export const createPaymentAmountOption = (paymentOption, apiContext) => {
    const paymentAmount = getPaymentAmountFromOption(paymentOption, apiContext);
    const paymentAmountOptionText = t(`paymentAmountOptionText.${paymentOption}`);
    const optionSubtext = t(`paymentAmountOptionSubText.${paymentOption}`, '');
    const paymentAmountOptionSubtext = optionSubtext ? ` (${optionSubtext})` : '';
    return {
        element: (_jsxs(PaymentOption, Object.assign({ vertical: "base" }, { children: [_jsx(Amount, Object.assign({ "data-cy": "payment-amount", isInline: true }, { children: formatCurrency(Number(paymentAmount)) })), _jsx(Label, Object.assign({ "data-cy": `${paymentOption}.label`, isInline: true }, { children: paymentAmountOptionSubtext
                        ? `${paymentAmountOptionText}${paymentAmountOptionSubtext}`
                        : paymentAmountOptionText }))] }))),
        value: paymentOption,
        label: getPaymentAmountDisplay(paymentOption, paymentAmount),
        dataCy: paymentOption
    };
};
/**
 * This helper format the payment methods by combining
 * nick name, method type and last four digits of account number
 * @param method
 */
const formatPaymentMethodOptions = (paymentMethods) => {
    return paymentMethods.map((method) => {
        return {
            label: formatDisplayAccount(method),
            value: `${method.id}`,
            dataCy: `payment-method-option.${method.id}`
        };
    });
};
/**
 * This helper return payment method options
 * @param paymentMethods
 * @param setShowAddPaymentMethod
 * @param managePaymentMethodClicked
 */
export const getPaymentMethodOptions = (paymentMethods, setShowAddPaymentMethod, displayManagePaymentMethods) => {
    const paymentMethodOptions = formatPaymentMethodOptions(paymentMethods);
    const optionText = displayManagePaymentMethods
        ? 'managePaymentMethod'
        : 'addNewPaymentMethod';
    const dataReason = displayManagePaymentMethods
        ? DATA_REASON.PAYMENT_METHOD.MANAGE_PAYMENT_METHOD
        : DATA_REASON.PAYMENT_METHOD.ADD_PAYMENT_METHOD;
    const addNewPaymentMethod = {
        element: (_jsxs(PaymentMethodOption, Object.assign({ "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-cy": `payment-method-field-link.${optionText}`, "data-object": DATA_OBJECT.BUTTON, "data-reason": dataReason, "data-type": DATA_TYPE.MAKE_A_PAYMENT }, { children: [t(`button.${optionText}`), _jsx(FontAwesomeIcon, { icon: FAS_EXTERNAL_LINK, size: "lg" }), _jsx(IconInfoContainer, { children: t('label.thisOpensANewPage') })] }))),
        onClick: setShowAddPaymentMethod,
        label: t(`button.${optionText}`) + t('label.thisOpensANewPage'),
        value: ''
    };
    paymentMethodOptions.push(addNewPaymentMethod);
    return paymentMethodOptions;
};
export const getPaymentOption = (options, value) => options.find((option) => `${option.value}` === `${value}`);
export const getOneTimeOtherAmountHelpText = (amount, paymentAmount, isSettlement) => {
    if (Number.isNaN(amount)) {
        return '';
    }
    const isLowerThanMonthlyDue = Number(amount) > 0 && Number(amount) < Number(paymentAmount);
    if (isLowerThanMonthlyDue && !isSettlement) {
        return t('makePayment.otherAmountHelpText');
    }
    return '';
};
export const getOtherAmountHelpText = (amount, paymentAmount) => {
    if (Number.isNaN(amount)) {
        return '';
    }
    const isLowerThanMonthlyDue = Number(amount) > 0 && Number(amount) < Number(paymentAmount);
    if (isLowerThanMonthlyDue) {
        return t('legal.autoIncreasePaymentRevolving');
    }
    return '';
};
export const getHighlightedDates = (featureFlags, isPastDue, dueDate) => {
    if (!dueDate) {
        return [];
    }
    const dueDateObject = formatDateStringToObject(dueDate);
    const isPastDueDate = isPastDue ? 'pastDueDate' : 'dueDate';
    const dueDateLegendText = t(`makePayment.${isPastDueDate}`);
    if (!featureFlags.isLateFeeDateEnabled) {
        return [
            {
                dates: [dueDateObject],
                themeName: isPastDueDate,
                legendText: dueDateLegendText
            }
        ];
    }
    const lateFeeDate = dueDate && addDays(dueDateObject, 1);
    return [
        {
            dates: [dueDateObject],
            themeName: isPastDueDate,
            legendText: dueDateLegendText
        },
        {
            dates: [lateFeeDate],
            themeName: 'lateFeeDate',
            legendText: t('makePayment.lateFeeDate')
        }
    ];
};
const createPaymentTypeOption = (value, labelKey, dataCy) => ({
    value: paymentTypes[value],
    label: t(labelKey),
    dataType: DATA_TYPE.MAKE_A_PAYMENT,
    dataObject: DATA_OBJECT.LIST,
    dataReason: DATA_REASON.PAYMENT_TYPE[value.toUpperCase()],
    dataAnalytics: 'clicktrackingevent',
    dataCy
});
export const getPaymentTypeOptions = ({ isAutopayEnrolled, isAutopayEnrollEligible, isSettlement, productType, featureFlags }) => {
    const oneTimePaymentTypeOption = createPaymentTypeOption('ONCE', isSettlement
        ? 'makePayment.paymentTypeOneTimeSettlement'
        : 'makePayment.paymentTypeOneTime', 'payment-type.one-time');
    const autopayPaymentTypeOption = createPaymentTypeOption('AUTOPAY', 'makePayment.paymentTypeAutopay', 'payment-type.autopay');
    const payoffPaymentTypeOption = createPaymentTypeOption('PAYOFF', 'makePayment.paymentTypePayoff', 'payment-type.pay-off');
    const showAutopayPaymentOptions = !isAutopayEnrolled && isAutopayEnrollEligible;
    let paymentTypeOptions = [oneTimePaymentTypeOption];
    if (isSecuredInstallmentLoanProduct(productType) &&
        !featureFlags.isSecuredInstallmentAutopayEnabled) {
        return paymentTypeOptions;
    }
    if (showAutopayPaymentOptions) {
        paymentTypeOptions = [...paymentTypeOptions, autopayPaymentTypeOption];
    }
    if (!isSecuredInstallmentLoanProduct(productType) &&
        isInstallmentLoan(productType)) {
        paymentTypeOptions = [...paymentTypeOptions, payoffPaymentTypeOption];
    }
    return paymentTypeOptions;
};
export const getAutopayPaymentAmountHelpText = (productType, paymentEnterpriseEligibility, fixedPaymentAmount, paymentOption) => {
    const shouldDisplayHelpTextForPaymentAmount = [
        paymentOptions.STATEMENT_BALANCE,
        paymentOptions.MINIMUM_PAYMENT_DUE,
        paymentOptions.SECURED_INSTALLMENT_FIXED_PAYMENT_AMOUNT
    ].includes(paymentOption);
    if (isMonthly(productType) &&
        isIntroRateLoanPromoAvailableForAutopay(paymentEnterpriseEligibility)) {
        return t('autopay.introRateLoanAutopayText', {
            fixedPaymentAmount
        });
    }
    if (shouldDisplayHelpTextForPaymentAmount) {
        if (isSecuredInstallmentLoanProduct(productType)) {
            return t('autopay.autopayPaymentAmountHelpTextForSI');
        }
        return t('autopay.autopayPaymentAmountHelpText');
    }
    return '';
};
